/**
 * The rank-up's confetti: one mesh, one draw, every flake a function of the
 * ceremony's clock.
 *
 * Nothing is simulated per frame and nothing is allocated while it runs — each
 * flake's whole life (when it is thrown, where, how it tumbles, when it fades)
 * is derived in the vertex shader from its own seed and the single time the
 * frame hands in. A pinned clock therefore captures the identical flurry every
 * time, which is what makes the clips comparable between runs.
 *
 * The colours are the deck's: the red suits' ink, the stock's white, the rank's
 * own colour and a gold. Black flakes are not in the set on purpose — the veil
 * is dark, and a black flake on it is a hole, not a flake.
 */

import { Buffer, BufferUsage, Geometry, GlProgram, Mesh, Shader, UniformGroup } from 'pixi.js';

const VERT = /* glsl */ `
in vec2 aCorner;   // -1..1 within the flake
in vec4 aSeed;     // angle, speed, spin, birth (0..1 of the emit window)
in float aColour;  // 0..3, into uColours

out vec2 vCorner;
out vec3 vTint;
out float vAlpha;
out float vFacing;

uniform mat3 uProjectionMatrix;
uniform mat3 uWorldTransformMatrix;
uniform mat3 uTransformMatrix;
uniform vec4 uOrigin;   // x, y (px), flake size (px), gravity (px/s^2)
uniform vec4 uClock;    // t (s since the first flake), emit window (s), life (s), fade (s)
uniform vec3 uColours[4];
uniform float uKeep;    // flakes fade out above this y (px): none ever lands on the banner under the card

void main() {
  float birth = aSeed.w * uClock.y;
  float age = uClock.x - birth;
  if (age < 0.0 || age > uClock.z) {
    // Not yet thrown, or gone: collapse the quad so it costs no fragments.
    gl_Position = vec4(2.0, 2.0, 0.0, 1.0);
    vAlpha = 0.0;
    vTint = vec3(0.0);
    vCorner = aCorner;
    vFacing = 1.0;
    return;
  }

  float ang = aSeed.x;
  float speed = aSeed.y;
  vec2 vel = vec2(cos(ang), sin(ang)) * speed;
  // Air drag, so the burst slows and the fall takes over.
  float drag = 1.0 - exp(-age * 1.9);
  vec2 pos = uOrigin.xy + vel * drag / 1.9 + vec2(0.0, 0.5 * uOrigin.w * age * age);
  // A slow sway, different per flake.
  pos.x += sin(age * (1.4 + aSeed.z) + aSeed.x * 6.0) * 14.0;

  // The tumble: the flake turns about its own long axis, so it goes edge-on
  // and all but vanishes twice a turn — the thing that makes paper read as paper.
  float spin = age * (2.2 + aSeed.z * 5.0) + aSeed.x;
  float squash = cos(spin);
  float roll = aSeed.z * 2.0 + age * 0.7;
  vec2 c = vec2(aCorner.x * abs(squash), aCorner.y);
  vec2 r = vec2(c.x * cos(roll) - c.y * sin(roll), c.x * sin(roll) + c.y * cos(roll));
  vec2 p = pos + r * uOrigin.z;

  mat3 mvp = uProjectionMatrix * uWorldTransformMatrix * uTransformMatrix;
  gl_Position = vec4((mvp * vec3(p, 1.0)).xy, 0.0, 1.0);
  vCorner = aCorner;
  int ci = int(aColour + 0.5);
  vTint = uColours[ci];
  vAlpha = smoothstep(0.0, 0.08, age) * (1.0 - smoothstep(uClock.z - uClock.w, uClock.z, age));
  // The keep-out: the whole flake (its lowest corner, not its centre) is gone before the line, from the first frame to
  // the last, so no flake is ever on the banner (a hard 0 by the line, a soft fade over 90 px above it).
  vAlpha *= 1.0 - smoothstep(uKeep - 100.0, uKeep - 12.0, p.y + uOrigin.z);
  vFacing = abs(squash);
}
`;

const FRAG = /* glsl */ `
precision mediump float;
in vec2 vCorner;
in vec3 vTint;
in float vAlpha;
in float vFacing;
out vec4 finalColor;

void main() {
  if (vAlpha <= 0.002) discard;
  // Rounded corners, and the face darkens as it turns away from the light.
  vec2 q = abs(vCorner) - vec2(0.72, 0.28);
  float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - 0.26;
  float mask = 1.0 - smoothstep(-0.05, 0.05, d);
  if (mask <= 0.01) discard;
  // Foil, not paper: a flake in the rank's colour that flashes white as it turns square to the light.
  vec3 col = vTint * (0.5 + 0.55 * vFacing) + vec3(1.0, 0.96, 0.84) * pow(vFacing, 9.0) * 0.75;
  finalColor = vec4(col, 1.0) * (mask * vAlpha);
}
`;

let program: GlProgram | null = null;

export interface ConfettiFrame {
  /** Seconds since the first flake was thrown. */
  readonly time: number;
  /** Where the flakes come from, in the container's pixels. */
  readonly x: number;
  readonly y: number;
  /** 0 while nothing has been thrown yet. */
  readonly alpha: number;
  /** Flakes fade out as they fall to this y (px), so none lands on the banner (default: no limit). */
  readonly keepAbove?: number;
}

/** How long one flake falls, and how long it takes to fade out at the end. */
const LIFE_S = 2.9;
const FADE_S = 0.9;

export class Confetti extends Mesh<Geometry, Shader> {
  readonly #uniforms: UniformGroup;

  /**
   * `count` flakes, thrown over `emitSeconds`. The quality tier picks the
   * count; everything else is the same on every device, so a low-count device
   * sees a thinner flurry of the identical shape.
   */
  constructor(count: number, emitSeconds: number, colours: readonly [number, number, number][], size = 10) {
    program ??= new GlProgram({ vertex: VERT, fragment: FRAG, name: 'w21-ceremony-confetti' });

    const corners = new Float32Array(count * 8);
    const seeds = new Float32Array(count * 16);
    const tints = new Float32Array(count * 4);
    const indices = new Uint16Array(count * 6);
    // A fixed, reproducible scatter: mulberry32 on the flake's index, so the
    // same ceremony throws the same confetti on every device and every capture.
    let state = 0x2117ee5d >>> 0;
    const rnd = (): number => {
      state = (state + 0x6d2b79f5) >>> 0;
      let t = state;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
    for (let i = 0; i < count; i++) {
      // Up and out, biased upward: it is thrown, not dropped.
      const ang = -Math.PI * (0.08 + 0.84 * rnd());
      const speed = 480 + rnd() * 1150;
      const spin = rnd();
      const birth = rnd();
      const colour = Math.floor(rnd() * colours.length);
      for (let k = 0; k < 4; k++) {
        const cx = k === 0 || k === 3 ? -1 : 1;
        const cy = k < 2 ? -1 : 1;
        corners[(i * 4 + k) * 2] = cx;
        corners[(i * 4 + k) * 2 + 1] = cy;
        seeds[(i * 4 + k) * 4] = ang;
        seeds[(i * 4 + k) * 4 + 1] = speed;
        seeds[(i * 4 + k) * 4 + 2] = spin;
        seeds[(i * 4 + k) * 4 + 3] = birth;
        tints[i * 4 + k] = colour;
      }
      const v = i * 4;
      indices.set([v, v + 1, v + 2, v, v + 2, v + 3], i * 6);
    }

    const flat = new Float32Array(12);
    for (let i = 0; i < 4; i++) {
      const c = colours[Math.min(i, colours.length - 1)] ?? [1, 1, 1];
      flat[i * 3] = c[0];
      flat[i * 3 + 1] = c[1];
      flat[i * 3 + 2] = c[2];
    }

    const uniforms = new UniformGroup({
      uOrigin: { value: new Float32Array([0, 0, size, 1500]), type: 'vec4<f32>' },
      uClock: { value: new Float32Array([-1, emitSeconds, LIFE_S, FADE_S]), type: 'vec4<f32>' },
      uColours: { value: flat, type: 'vec3<f32>', size: 4 },
      uKeep: { value: 100000, type: 'f32' },
    });

    super({
      geometry: new Geometry({
        attributes: {
          aCorner: { buffer: new Buffer({ data: corners, usage: BufferUsage.VERTEX }), format: 'float32x2' },
          aSeed: { buffer: new Buffer({ data: seeds, usage: BufferUsage.VERTEX }), format: 'float32x4' },
          aColour: { buffer: new Buffer({ data: tints, usage: BufferUsage.VERTEX }), format: 'float32' },
        },
        indexBuffer: new Buffer({ data: indices, usage: BufferUsage.INDEX | BufferUsage.COPY_DST }),
      }),
      shader: new Shader({ glProgram: program, resources: { confettiUniforms: uniforms } }),
    });
    this.#uniforms = uniforms;
    this.visible = false;
  }

  draw(f: ConfettiFrame): void {
    const origin = this.#uniforms.uniforms.uOrigin as Float32Array;
    origin[0] = f.x;
    origin[1] = f.y;
    const clock = this.#uniforms.uniforms.uClock as Float32Array;
    clock[0] = f.time;
    this.#uniforms.uniforms.uKeep = f.keepAbove ?? 100000;
    this.#uniforms.update();
    this.alpha = f.alpha;
    this.visible = f.alpha > 0.002 && f.time >= 0 && f.time < LIFE_S + clock[1]!;
  }
}
